import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import useToast from "../../../../util/useToast";
import {useCallback, useEffect, useState} from "react";
import {MODE_EDIT, RULE_TYPE_CONTAINS} from "../../../../codes";
import {editorConfigSelector, rxHideRuleEditor, rxShowHideDeleteConfirmationModal, rxUpdateRules} from "../../../../slices/rulesSlice";
import {createTransactionRule, deleteTransactionRule, updateTransactionRule} from "../../../../api/ApiTransactionRules";
import {selectAllCategories} from "../../../../slices/categoriesSlice";
import {getOrDefault} from "../../../../util/util";
import useNetwork from "../../../../util/useNetwork";
import useValidator from "../../../../util/useValidator";

const useRuleEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("settings_rules");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const {show, mode, rule, deleteConfirmationModal} = useSelector(editorConfigSelector);

  const transactionCategories = useSelector(selectAllCategories);

  const [editedRule, setEditedRule] = useState({errors: {}, value: "", payee: "", tags: ""});
  const [availableCategories, setAvailableCategories] = useState([]);

  const hideRuleEditor = () => dispatch(rxHideRuleEditor());

  useEffect(() => {
    if (!rule || !show) {
      return;
    }

    const values = {
      errors: {},
      type: getOrDefault(rule.type, RULE_TYPE_CONTAINS),
      value: getOrDefault(rule.value, ""),
      payee: getOrDefault(rule.payee, ""),
      category: getOrDefault(rule.category, null),
      tags: getOrDefault(rule.tags, []).join(" ")
    };

    setEditedRule(values);

    setAvailableCategories(() => transactionCategories.filter(c => c.active || c.id === values.category));
  }, [show, rule, transactionCategories]);

  const getErrors = values => {
    const errors = {};

    if (!values.value.trim().length) {
      errors.value = "rule_editor_error_value_must_be_filled";
    }

    if (!values.payee.trim().length && !values.category && values.tags.length === 0) {
      errors.payee = "rule_editor_error_payee_or_category_or_tag_must_be_filled";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    const values = {
      type: editedRule.type,
      value: editedRule.value,
      payee: editedRule.payee,
      category: editedRule.category,
      tags: editedRule.tags.split(" ").map(tag => tag.trim()).filter(tag => tag.length > 0)
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateTransactionRule(rule.id, values);
        } else {
          return createTransactionRule(values);
        }
      })
      .then(result => dispatch(rxUpdateRules(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "rule_edited" : "rule_added"));

        hideRuleEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedRule(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedRule = field => event => {
    const values = {};

    switch (field) {
      case "category":
        values.category = event;
        break;
      case "type":
        values.type = event;
        break;
      case "tags": {
        const tags = event.target.value
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replaceAll(/[^0-9a-zA-Z\s]/g, " ")
          .toLowerCase()
          .replace(/\s\s+/g, " ");

        if (!/^[0-9a-zA-Z\s]*$/.test(tags)) {
          return;
        }

        values.tags = tags;

        break;
      }
      default:
        values[field] = event.target.value;
        break;
    }

    setEditedRule(prev => ({...prev, ...values}));
  };

  const showDeleteConfirmationModal = useCallback(() => dispatch(rxShowHideDeleteConfirmationModal(true)), [dispatch]);
  const hideDeleteConfirmationModal = useCallback(() => dispatch(rxShowHideDeleteConfirmationModal(false)), [dispatch]);

  const onDeleteRule = () => {
    return deleteTransactionRule(rule.id)
      .then(result => {
        dispatch(rxUpdateRules(result));
        dispatch(rxHideRuleEditor());
      })
      .catch(handleNetworkError)
      .finally(hideDeleteConfirmationModal);
  };

  return {
    show,
    t,
    mode,
    hideRuleEditor,
    editedRule,
    updateEditedRule,
    submit,
    availableCategories,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteRule
  };
};

export default useRuleEditor;
